import React, { useEffect, useRef, useState } from "react";
import Logo from "../assets/loginLogo.svg";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { LogOut, CircleUserRound, MoreVertical } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../store/userSlice";
import { useDispatch } from "react-redux";
import { displayImage } from "../utils";
import SidebarWarningStrip from "./SidebarWarnings";
import { getOverLimitChecks } from "../store/userSlice";
const Siderbar = ({ menuItems }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const { user } = useSelector((state) => {
    return state.user;
  });
  const limitChecks = useSelector(getOverLimitChecks);
  const [openWarning, setOpenWarning] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const warnings = [
    { type: "users", condition: limitChecks.usersExceeded },
    { type: "mappings", condition: limitChecks.mappingsExceeded },
  ];
  return (
    <>
      {/* Hamburger for mobile */}
      <button
        className="md:hidden fixed top-4 left-4 z-20 bg-white border border-gray-300 rounded p-2 shadow"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open sidebar"
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          w-72 bg-[#FAFAFA] p-4 flex flex-col justify-between border-r border-gray-300 font-raleway
          fixed top-0 left-0 h-full z-20 transition-transform duration-200
          ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0
          md:static  sm:h-auto sm:w-72
        `}
        style={{ minHeight: "100vh" }}
      >
        <nav>
          <Link to="/">
            <img
              src={Logo}
              alt="Logo"
              className="h-[75px] select-none -mt-3 "
              draggable="false"
            />
          </Link>
          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <li key={index} className="mb-2 ">
                <Link
                  to={item.path}
                  className={`block py-3 px-4 rounded transition-all 
                    ${
                      location.pathname === item.path
                        ? "bg-lightBlue text-white  shadow-md"
                        : "text-neutral-900 hover:bg-lightGreen hover:text-white"
                    }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="inline-block ml-2" size={20} />
                  <span className="ml-2"> {item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Profile Dropdown */}
        <div className="relative space-y-2" ref={profileRef}>
          {user.role === "super_admin" &&
            warnings
              .filter((w) => w.condition)
              .map((w) => (
                <SidebarWarningStrip
                  key={w.type}
                  type={w.type}
                  overLimitSince={user?.companyData?.overLimitSince}
                  isOpen={openWarning === w.type}
                  onToggle={() =>
                    setOpenWarning((prev) => (prev === w.type ? null : w.type))
                  }
                />
              ))}
          <button
            onClick={() => {
              setShowProfileMenu((prev) => !prev);
            }}
            className="w-full flex items-center gap-2 py-2 px-4 rounded bg-gray-50 border border-gray-400 hover:bg-gray-200"
          >
            <img
              src={displayImage(user?.profileImage?.data)}
              alt="Profile"
              className="w-8 h-8 rounded-full object-cover"
            />

            <span>{user?.firstName || ""}</span>
            <MoreVertical className="ml-auto" size={20} />
          </button>

          {showProfileMenu && (
            <div className="absolute bottom-11 w-full rounded border bg-gray-50 border-gray-400 z-20">
              <button
                onClick={() => {
                  dispatch(logout());
                  navigate("/portal/login", { replace: true });
                  localStorage.removeItem("token");
                }}
                className="flex items-center text-red-600 hover:bg-gray-200 w-full px-4 py-2 border-b border-gray-400 "
              >
                <LogOut className="mr-2" size={20} /> Logout
              </button>
              <Link
                onClick={() => setSidebarOpen(false)}
                to="/portal/profile"
                className={`flex items-center w-full px-4 py-2 hover:bg-gray-200 active:bg-lightBlue active:text-white rounded  transition-colors duration-100`}
              >
                <CircleUserRound className="mr-2" size={20} /> Profile Settings
              </Link>

              {user?.role === "super_admin" && (
                <Link
                  onClick={() => setSidebarOpen(false)}
                  to="/portal/account"
                  className={`flex items-center w-full px-4 py-2 hover:bg-gray-200 active:bg-lightBlue active:text-white rounded  transition-colors duration-100`}
                >
                  <CircleUserRound className="mr-2" size={20} /> Account
                  Settings
                </Link>
              )}

              {/* <Link
                onClick={() => setSidebarOpen(false)}
                to="/portal/plan"
                className={`flex items-center w-full px-4 py-2 hover:bg-gray-200 active:bg-lightBlue active:text-white rounded  transition-colors duration-100`}
              >
                <CircleUserRound className="mr-2" size={20} /> About Plan
              </Link> */}
            </div>
          )}
        </div>

        {/* Close button for mobile */}
        <button
          className="md:hidden absolute top-4 right-4 text-gray-600 bg-white border border-gray-300 rounded p-1"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      </aside>
    </>
  );
};
export default Siderbar;
